import { useMemo } from 'react';
import { getActiveLocale } from '@/lib/locale';

export interface FormatValueProps {
  value: number;
  unitType?: string;
  unitSymbol?: string;
  unitLabel?: string;
  decimals?: number;
  compact?: boolean;
}

function formatNumber(
  value: number,
  unitType?: string,
  unitSymbol?: string,
  unitLabel?: string,
  decimals?: number,
  compact?: boolean,
): string {
  const opts: Intl.NumberFormatOptions = {
    maximumFractionDigits: decimals ?? 2,
    minimumFractionDigits: decimals ?? 0,
  };

  if (compact) {
    opts.notation = 'compact';
  }

  switch (unitType) {
    case 'currency': {
      const formatted = new Intl.NumberFormat(getActiveLocale(), opts).format(value);
      return unitSymbol ? `${unitSymbol}${formatted}` : formatted;
    }
    case 'percentage': {
      const formatted = new Intl.NumberFormat(getActiveLocale(), opts).format(value);
      return `${formatted}%`;
    }
    case 'count': {
      const formatted = new Intl.NumberFormat(getActiveLocale(), opts).format(value);
      return unitLabel ? `${formatted} ${unitLabel}` : formatted;
    }
    case 'quantity': {
      const formatted = new Intl.NumberFormat(getActiveLocale(), opts).format(value);
      return unitSymbol ? `${formatted} ${unitSymbol}` : formatted;
    }
    default: {
      return new Intl.NumberFormat(getActiveLocale(), opts).format(value);
    }
  }
}

export function FormatValue({
  value,
  unitType,
  unitSymbol,
  unitLabel,
  decimals,
  compact,
}: FormatValueProps) {
  const formatted = useMemo(
    () => formatNumber(value, unitType, unitSymbol, unitLabel, decimals, compact),
    [value, unitType, unitSymbol, unitLabel, decimals, compact],
  );

  return <>{formatted}</>;
}

export { formatNumber };
